import React, { useState, useEffect } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Paper,
  Box,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Icon from "../../assets/img/icon.png";
import Banner from "../../assets/img/tagitstore_banner-01.jpg";
import authApi from "../../services/auth";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import { showSnackbar } from "../../redux/snackbar/actions";
import { encryptAES } from "../../utils";

const useStyles = makeStyles((theme) => ({
  btn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bannerImage: {
    backgroundSize: "cover",
    backgroundPosition: "right",
    [theme.breakpoints.up("md")]: {
      backgroundImage: `url(${Banner})`,
      backgroundPosition: "left",
      backgroundSize: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  bg: {
    backgroundColor: "#f7f4f2",
  },
}));

function base64UrlDecode(base64Url) {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const decodedString = atob(base64);
  return decodedString;
}

function formatDate(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const ResetPassword = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { token: pathToken } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const queryToken = queryParams.get("token");

  const token = pathToken || queryToken;

  const [decodedToken, setDecodedToken] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [expirationDate, setExpirationDate] = useState(null);

  useEffect(() => {
    if (token) {
      try {
        const [header, payload] = token.split(".");

        const decodedPayload = base64UrlDecode(payload);
        const payloadObj = JSON.parse(decodedPayload);
        setDecodedToken(payloadObj);
        const { exp, iat } = payloadObj;
        setExpirationDate(formatDate(exp));
        const isExpired = exp * 1000 < Date.now();
        setIsTokenExpired(isExpired);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [token]);

  const [showPassword, setShowPassword] = useState(false);
  const [showCnfPassword, setShowCnfPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const toggleCnfPasswordVisibility = () => {
    setShowCnfPassword((prevShowCnfPassword) => !prevShowCnfPassword);
  };

  let lastSubmitTime = 0;
    const [userInteracted, setUserInteracted] = useState(false);
    
    useEffect(() => {
      const handleUserInteraction = () => setUserInteracted(true);
    
      // document.addEventListener("mousemove", handleUserInteraction);
      document.addEventListener("keydown", handleUserInteraction);
      document.addEventListener("scroll", handleUserInteraction);
      document.addEventListener("touchstart", handleUserInteraction);
    
      return () => {
        // document.removeEventListener("mousemove", handleUserInteraction);
        document.removeEventListener("keydown", handleUserInteraction);
        document.removeEventListener("scroll", handleUserInteraction);
        document.removeEventListener("touchstart", handleUserInteraction);
      };
    }, []);
    
    
    const isBotDetected = () => {
      // Skip bot detection for mobile devices
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          return false;
      }
  
      // Check for common bot indicators only on desktop
      return (
          (navigator.webdriver && !/Android|iPhone/i.test(navigator.userAgent)) ||
          (!navigator.languages || navigator.languages.length === 0) ||
          (/bot|crawler|spider|headless|phantom/i.test(navigator.userAgent) && 
           !/Chrome|Firefox|Safari/i.test(navigator.userAgent)) ||
          /HeadlessChrome|puppeteer|phantomjs/i.test(navigator.userAgent) ||
          (!userInteracted && !/Android|iPhone/i.test(navigator.userAgent))
      );
  };

  const handleSubmit = (formData) => {
    const now = Date.now();
  
    if (isBotDetected()) {
      alert("Bot access is restricted!");
      window.location.reload();
      return;
    }
  
    if (now - lastSubmitTime < 3000) {
      alert("Multiple rapid submissions detected!");
      window.location.reload();
      return;
    }
  
    lastSubmitTime = now;
  
    if (formData?.honeypot) {
      alert("Suspicious activity detected!");
      return;
    }
    dispatch(showLoader());
    const data = {
      token: token,
      password: formData?.password,
    };

    const encryptedData = encryptAES(data);
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg,
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        navigate("/");
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Link expired",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    authApi.setPassword(encryptedData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      cnfpassword: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      password: yup
        .string()
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&^#])[A-Za-z\d@$!%*?&^#]{8,12}$/,
          "Password must be 8-12 characters, include at least one letter, one number, and one special character"
        )
        .required("Required"),
      cnfpassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  if (isTokenExpired) {
    return (
      <Grid
        container
        component="main"
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CssBaseline />
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          component={Paper}
          elevation={6}
          square
          className={classes.bg}
        >
          <Box
            sx={{
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "70vh",
            }}
          >
            <Typography variant="h5" color="error" component="h1">
              Link has expired!
            </Typography>
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              The link expired on: {expirationDate}.
              <br />
              Please request a new password reset link.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid item xs={false} sm={null} md={8} className={classes.bannerImage} />
      <Grid
        item
        xs={12}
        sm={12}
        md={4}
        pt={5}
        component={Paper}
        elevation={6}
        square
        className={classes.bg}
      >
        <Box
          sx={{
            my: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={Icon}
            alt="logo"
            sx={{
              my: 2,
              width: "75px",
              height: "auto",
              maxWidth: "100%",
            }}
          />
          <Typography component="h1" variant="h5">
            Set Password
          </Typography>
          <Box sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="off"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="cnfpassword"
              label="Confirm Password"
              name="cnfpassword"
              type={showCnfPassword ? "text" : "password"}
              autoComplete="off"
              value={formik.values.cnfpassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.cnfpassword && Boolean(formik.errors.cnfpassword)
              }
              helperText={
                formik.touched.cnfpassword && formik.errors.cnfpassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={toggleCnfPasswordVisibility}
                      edge="end"
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{
                height: "100%",
                background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                color: "#fff",
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                textTransform: "uppercase",
                fontWeight: "bold",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                },
              }}
              onClick={formik.handleSubmit}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
